import { useEffect, useState } from 'react'
import { getWorkCategories } from '../../api/workApi'
import type { WorkCategoryInfo, WorkWizardState } from '../../types/work'
import WorkWizardStep1 from './WorkWizardStep1'
import WorkWizardStep2 from './WorkWizardStep2'
import WorkWizardStep3 from './WorkWizardStep3'
import WorkWizardStep4 from './WorkWizardStep4'

const STEPS = ['Category', 'Details', 'File', 'Confirm']

const INITIAL_STATE: WorkWizardState = {
  step: 1,
  category: undefined,
  subcategory: undefined,
  title: '',
  description: '',
  input_text: '',
  input_params: {},
  file: undefined,
  estimate: undefined,
}

export default function WorkWizard() {
  const [state, setState] = useState<WorkWizardState>(INITIAL_STATE)
  const [categories, setCategories] = useState<WorkCategoryInfo[]>([])

  useEffect(() => {
    getWorkCategories().then(setCategories).catch(() => {})
  }, [])

  function patch(update: Partial<WorkWizardState>) {
    setState(prev => ({ ...prev, ...update }))
  }

  function goNext() {
    setState(prev => ({ ...prev, step: Math.min(4, prev.step + 1) as WorkWizardState['step'] }))
  }

  function goBack() {
    setState(prev => ({ ...prev, step: Math.max(1, prev.step - 1) as WorkWizardState['step'] }))
  }

  const selectedCategory = categories.find(c => c.id === state.category)
  const selectedSub = selectedCategory?.subcategories.find(s => s.id === state.subcategory)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Progress bar */}
      <div>
        <div style={{ display: 'flex', gap: '0', marginBottom: '8px' }}>
          {STEPS.map((label, i) => {
            const stepNum = i + 1
            const isActive = state.step === stepNum
            const isDone = state.step > stepNum
            return (
              <div
                key={label}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  position: 'relative',
                }}
              >
                {/* Connector line */}
                {i > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '12px',
                      left: '-50%',
                      width: '100%',
                      height: '2px',
                      background: isDone ? 'var(--primary, #6366f1)' : 'var(--border-color)',
                      transition: 'background 0.3s',
                    }}
                  />
                )}
                {/* Circle */}
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: isActive || isDone ? 'var(--primary, #6366f1)' : 'var(--card-bg)',
                    border: `2px solid ${isActive || isDone ? 'var(--primary, #6366f1)' : 'var(--border-color)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: isActive || isDone ? '#fff' : 'var(--text-muted, #6b7280)',
                    zIndex: 1,
                    transition: 'background 0.3s, border-color 0.3s',
                  }}
                >
                  {isDone ? '✓' : stepNum}
                </div>
                {/* Label */}
                <span
                  style={{
                    fontSize: '11px',
                    color: isActive ? 'var(--primary, #6366f1)' : 'var(--text-muted, #6b7280)',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Step content */}
      <div>
        {state.step === 1 && (
          <WorkWizardStep1 state={state} onChange={patch} onNext={goNext} />
        )}
        {state.step === 2 && (
          <WorkWizardStep2 state={state} onChange={patch} onNext={goNext} onBack={goBack} />
        )}
        {state.step === 3 && (
          <WorkWizardStep3
            state={state}
            subcategoryInfo={selectedSub}
            onChange={patch}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {state.step === 4 && (
          <WorkWizardStep4
            state={state}
            subcategoryInfo={selectedSub}
            onBack={goBack}
          />
        )}
      </div>
    </div>
  )
}
